"use client";
import { Timeline, Typography, Table, Tag, Divider } from "antd";
import {
  EditOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  PlusOutlined,
  SolutionOutlined,
  ScissorOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  CloseOutlined,
  MedicineBoxOutlined,
} from "@ant-design/icons";
import "bootstrap/dist/css/bootstrap.min.css";
import { getCaseSheetData } from "../../services/doctor/consultant/consultant";
import { Tabs, Button, Card, Grid } from "antd";
import { Row, Col } from "react-bootstrap";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { bookAppointment } from "../../services/receptionist/book appointment/bookappointment";
import { useAuth } from "../../services/authentication_API/authService";

const { Title, Text } = Typography;

// Add this to ensure Ant Design components have basic styling
const antdStyles = {
  primaryColor: "#1890ff",
  backgroundColor: "#f9f9f9",
  borderColor: "#d9d9d9",
};

export default function PatientHistory({ patientId }) {
  const [caseSheets, setCaseSheets] = useState([]);
  const [caseSheetIndex, setCaseSheetIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const screens = Grid.useBreakpoint();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Responsive font sizes
  const getFontSize = (base, sm, xs) => {
    if (screens.xs) return xs;
    if (screens.sm) return sm;
    return base;
  };

  const fetchCaseSheetData = async () => {
    if (!patientId) return;
    try {
      setLoading(true);
      const result = await getCaseSheetData(patientId);
      if (result.success) {
        setCaseSheets(result.data || []);
        setCaseSheetIndex(0);
      } else {
        setCaseSheets([]);
        setCaseSheetIndex(0);
        console.error(
          "Error:",
          result.error || "Failed to fetch casesheet data"
        );
      }
    } catch (err) {
      setCaseSheets([]);
      setCaseSheetIndex(0);
      console.error("Exception:", err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("PatientHistory patientId:", patientId);
    fetchCaseSheetData();
  }, [patientId]);

  const currentCase = caseSheets[caseSheetIndex] || {};

  return (
    <div className="container mt-4">
      <Card
        title={
          <span style={{ fontSize: getFontSize("18px", "17px", "16px") }}>
            Casesheet
          </span>
        }
        className="h-100"
        style={{ minHeight: screens.xs ? "400px" : "500px" }}
        extra={
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              icon={<LeftOutlined />}
              size="small"
              disabled={caseSheetIndex === caseSheets.length - 1}
              onClick={() =>
                setCaseSheetIndex((i) => Math.min(i + 1, caseSheets.length - 1))
              }
            />
            <Button
              icon={<RightOutlined />}
              size="small"
              disabled={caseSheetIndex === 0}
              onClick={() => setCaseSheetIndex((i) => Math.max(i - 1, 0))}
            />
          </div>
        }
      >
        <div
          style={{
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {loading ? (
            <div className="text-center py-5">Loading...</div>
          ) : caseSheets.length === 0 ? (
            <div className="text-center py-5">No Casesheet Data</div>
          ) : currentCase?.print_html_view == null ||
            currentCase?.print_html_view === undefined ? (
            <>
              <div className="consultation-date mb-3">
                <h5
                  style={{
                    fontSize: getFontSize("18px", "16px", "15px"),
                  }}
                >
                  {currentCase.appointment_date
                    ? dayjs(currentCase.appointment_date).format("DD MMM YYYY")
                    : ""}
                  {" - "}
                  {currentCase.appointment_time
                    ? " " + currentCase.appointment_time
                    : ""}
                </h5>
              </div>
              {currentCase.symptoms.length > 0 && (
                <div className="symptoms-section mb-2">
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    <FileSearchOutlined
                      style={{
                        color: "#ff4d4f",
                        fontSize: "15px",
                        marginRight: "5px",
                      }}
                    />
                    <b>Symptoms: </b>
                  </span>
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    {currentCase.symptoms
                      ?.map((s) => {
                        // Collect available fields dynamically
                        const details = [s.note, s.since, s.severity]
                          .filter(Boolean)
                          .join(" - ");
                        return details ? `${s.name} (${details})` : s.name;
                      })
                      .join(", ")}
                  </span>
                </div>
              )}
              {currentCase.findings.length > 0 && (
                <div className="diagnosis-section mb-2">
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    <SearchOutlined
                      style={{
                        color: "#faad14",
                        fontSize: "15px",
                        marginRight: "5px",
                      }}
                    />
                    <b>Findings: </b>
                  </span>
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    {currentCase.findings
                      ?.map((f) => {
                        // Dynamically build details just like for symptoms
                        const details = [f.note, f.since, f.severity]
                          .filter(Boolean)
                          .join(" - ");
                        return details ? `${f.name} (${details})` : f.name;
                      })
                      .join(", ")}
                  </span>
                </div>
              )}
              {currentCase.diagnosis.length > 0 && (
                <div className="diagnosis-section mb-2">
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    <SolutionOutlined
                      style={{
                        color: "#1890ff",
                        fontSize: "15px",
                        marginRight: "5px",
                      }}
                    />
                    <b>Diagnosis: </b>
                  </span>
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    {currentCase.diagnosis
                      ?.map((d) => {
                        // Dynamically include only available fields
                        const details = [d.note, d.location, d.description]
                          .filter(Boolean)
                          .join(" - ");
                        return details ? `${d.name} (${details})` : d.name;
                      })
                      .join(", ")}
                  </span>
                </div>
              )}

              {/* Medicines Table */}
              {currentCase.medicines.length > 0 && (
                <div className="diagnosis-section ">
                  {/* <span
                                        style={{
                                          fontSize: getFontSize("16px", "15px", "14px"),
                                        }}
                                      >
                                        <MedicineBoxOutlined
                                          style={{
                                            color: "#52c41a",
                                            fontSize: "15px",
                                            marginRight: "5px",
                                          }}
                                        />
                                        <b>Medicine: </b>
                                      </span> */}
                  <div
                    className="table-responsive "
                    style={{
                      overflowX: "auto",
                      maxHeight: "300px",
                      overflowY: "auto",
                    }}
                  >
                    <div className="">
                      <table className="table table-bordered">
                        <thead className="table-light">
                          <tr>
                            <th
                              style={{
                                width: "4%",
                                textAlign: "center",
                              }}
                            >
                              <strong>Rx</strong>
                            </th>
                            <th style={{ width: "26%" }}>
                              <strong>નામ</strong>
                            </th>
                            <th style={{ width: "17%" }}>
                              <strong>આવર્તન</strong>
                            </th>
                            <th style={{ width: "15%" }}>
                              <strong>સમયગાળો</strong>
                            </th>
                            <th style={{ width: "28%" }}>
                              <strong>નોંધ</strong>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentCase?.medicines?.map((med, idx) =>
                            med.doses?.map((dose, doseIdx) => (
                              <tr key={`${med.id}-${doseIdx}`}>
                                {doseIdx === 0 && (
                                  <td
                                    rowSpan={med.doses.length}
                                    className="text-center"
                                  >
                                    {idx + 1}
                                  </td>
                                )}
                                {doseIdx === 0 && (
                                  <td rowSpan={med.doses.length}>
                                    {med?.doses[0].type}{" "}
                                    <span style={{ fontWeight: "bold" }}>
                                      {med?.name}
                                    </span>
                                  </td>
                                )}
                                <td>
                                  {dose?.type_unit_ui == "Default" &&
                                  dose?.default_timing_instruction ? (
                                    <>
                                      {(() => {
                                        const { Breakfast, Lunch, Dinner } =
                                          dose.default_timing_instruction || {};
                                        const q = dose.quantity || 0;
                                        const breakfastDose = Breakfast ? q : 0;
                                        const lunchDose = Lunch ? q : 0;
                                        const dinnerDose = Dinner ? q : 0;
                                        const unitString =
                                          dose?.type &&
                                          !["Tablet", "Capsule"].includes(
                                            dose.type
                                          )
                                            ? ` ${dose?.unit || ""}`
                                            : "";

                                        return `${breakfastDose}${unitString} - ${lunchDose}${unitString} - ${dinnerDose}${unitString}`.trim();
                                      })()}
                                      <br />
                                    </>
                                  ) : dose?.type_unit_ui == "Alternate" &&
                                    dose?.default_timing_instruction ? (
                                    <>
                                      {(() => {
                                        const timingKeys = Object.keys(
                                          dose?.default_timing_instruction || {}
                                        );
                                        const key = timingKeys[0]; // Assume we only care about the first key if multiple exist

                                        // 2. Check for the specific frequency keys (in English) and return the corresponding Gujarati text
                                        if (key === "Once") {
                                          return `${dose?.quantity} ${dose?.unit} દિવસ માં ૧ વાર `; // Once a day
                                        }
                                        if (key === "Twice") {
                                          return `${dose?.quantity} ${dose?.unit} દિવસ માં ૨ વાર`; // Twice a day
                                        }
                                        if (key === "Thrice") {
                                          return `${dose?.quantity} ${dose?.unit} દિવસ માં ૩ વાર`; // Thrice a day
                                        }
                                        if (key === "4h") {
                                          return `${dose?.quantity} ${dose?.unit} દર ૪ કલાકે`; // Every 4 hours
                                        }
                                        if (key === "6h") {
                                          return `${dose?.quantity} ${dose?.unit} દર ૬ કલાકે`; // Every 6 hours
                                        }
                                        if (key === "8h") {
                                          return `${dose?.quantity} ${dose?.unit} દર ૮ કલાકે`; // Every 8 hours
                                        }
                                        if (key === "12h") {
                                          return `${dose?.quantity} ${dose?.unit} દર ૧૨ કલાકે`; // Every 12 hours
                                        }
                                        if (key === "48h") {
                                          return `${dose?.quantity} ${dose?.unit} દર ૨ દિવસે`; // Every 48 hours
                                        }
                                        return `${dose?.quantity} ${dose?.unit} ${key}`; // Default to the key if no match found
                                      })()}
                                    </>
                                  ) : (
                                    <>
                                      {dose?.quantity} {dose?.unit}
                                    </>
                                  )}
                                </td>
                                <td>
                                  {(() => {
                                    const duration =
                                      dose.duration?.toLowerCase() || "";

                                    if (
                                      duration.includes("d") ||
                                      duration.includes("day")
                                    ) {
                                      const num = duration.match(/\d+/)?.[0];
                                      return num ? `${num} દિવસ` : "1 દિવસ";
                                    } else if (
                                      duration.includes("w") ||
                                      duration.includes("week")
                                    ) {
                                      const num = duration.match(/\d+/)?.[0];
                                      return num
                                        ? `${num} અઠવાડિયા`
                                        : "1 અઠવાડિયા";
                                    } else if (
                                      duration.includes("m") ||
                                      duration.includes("month")
                                    ) {
                                      const num = duration.match(/\d+/)?.[0];
                                      return num ? `${num} મહિના` : "1 મહિના";
                                    } else {
                                      return dose.duration || "N/A";
                                    }
                                  })()}
                                </td>

                                <td>
                                  {dose?.type_unit_ui === "Default" // Changed == to the stricter ===
                                    ? (() => {
                                        const { Breakfast, Lunch, Dinner } =
                                          dose.default_timing_instruction || {};

                                        let timingParts = [];

                                        // Logic for Breakfast
                                        if (Breakfast) {
                                          timingParts.push(
                                            Breakfast === "Before"
                                              ? "નાસ્તા પહેલા"
                                              : "નાસ્તા પછી"
                                          );
                                        }

                                        // Logic for Lunch
                                        if (Lunch) {
                                          timingParts.push(
                                            Lunch === "Before"
                                              ? "બપોરે ભોજન પહેલા"
                                              : "બપોરે ભોજન પછી"
                                          );
                                        }

                                        // Logic for Dinner
                                        if (Dinner) {
                                          timingParts.push(
                                            Dinner === "Before"
                                              ? "રાત્રી ભોજન પહેલા"
                                              : "રાત્રી ભોજન પછી"
                                          );
                                        }

                                        const timingString =
                                          timingParts.join(", "); // Joins all parts with a comma and space

                                        // Combine timing and note.
                                        return (
                                          timingString +
                                          (dose.note ? `, ${dose.note}` : "")
                                        );
                                      })()
                                    : (() => {
                                        const timingValues = Object.values(
                                          dose?.default_timing_instruction || {}
                                        );
                                        const firstValue = timingValues[0];
                                        let timing = "";

                                        // Use if/else if for efficient and clear condition checking
                                        if (firstValue === "Before Food") {
                                          timing = "ભોજન પહેલા";
                                        } else if (
                                          firstValue === "After Food"
                                        ) {
                                          timing = "ભોજન પછી";
                                        } else if (firstValue === "With Food") {
                                          timing = "ભોજન સાથે";
                                        } else if (
                                          firstValue === "Empty Stomach"
                                        ) {
                                          timing = "ભૂખ્યા પેટે";
                                        } else if (firstValue === "Bedtime") {
                                          timing = "સુવા ના સમયે";
                                        } else {
                                          // Default case if the value is not recognized (optional)
                                          // You can set timing = firstValue or keep it empty
                                        }

                                        // Combine timing and note without unnecessary comma handling
                                        return (
                                          timing +
                                          (dose.note && timing
                                            ? `, ${dose.note}`
                                            : dose.note || "")
                                        );
                                      })()}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
              {currentCase.investigations.length > 0 && (
                <div className="investigations-section mb-2">
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    <FileTextOutlined
                      style={{
                        color: "#13c2c2",
                        fontSize: "15px",
                        marginRight: "5px",
                      }}
                    />
                    <b>Advised Investigations: </b>
                  </span>
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    {currentCase.investigations
                      ?.map((i) => i.name)
                      .join(", ") || "N/A"}
                  </span>
                </div>
              )}
              {currentCase.procedures.length > 0 && (
                <div className="investigations-section mb-2">
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    <ScissorOutlined
                      style={{
                        color: "#eb2f96",
                        fontSize: "15px",
                        marginRight: "5px",
                      }}
                    />
                    <b>Advised Procedures: </b>
                  </span>
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    {currentCase.procedures?.map((i) => i.name).join(", ") ||
                      "N/A"}
                  </span>
                </div>
              )}
              {currentCase.instructions.length > 0 && (
                <div className="investigations-section mb-2">
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    <EditOutlined
                      style={{
                        color: "#722ed1",
                        fontSize: "15px",
                        marginRight: "5px",
                      }}
                    />
                    <b>Instructions: </b>
                  </span>
                  <span
                    style={{
                      fontSize: getFontSize("16px", "15px", "14px"),
                    }}
                  >
                    {currentCase.procedures?.map((i) => i.name).join(", ") ||
                      "N/A"}
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <div
                dangerouslySetInnerHTML={{
                  __html: currentCase?.print_html_view || "<p>No Data</p>",
                }}
              />
            </>
          )}
        </div>
      </Card>
    </div>
  );
}

PatientHistory.propTypes = {
  patientId: PropTypes.number.isRequired,
};
